// src/contexts/AuthContext.js
import React, { useContext, useState, useEffect, useRef, useCallback, createContext } from 'react';
import { onAuthStateChanged, onIdTokenChanged, signOut as firebaseSignOut } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { API_BASE_URL } from '../apiConfig';
import { SUBSCRIBER_ROLES, hasPermission, hasAnyPermission, isRoleAtLeast, grantsEverything } from '../utils/subscriberRoles';
import { mergeStrictestPolicies } from '../utils/passwordPolicy';
import { NO_WORKSPACE_MESSAGE } from '../utils/accountMessages';
import { setAnalyticsUserContext } from '../services/analyticsService';
import { decodeJwtPayload } from '../utils/jwtPayload';
import {
  isSessionExpired,
  isSessionRevoked,
  SIGNED_OUT_REASON_KEY,
  SIGNED_OUT_EXPIRED,
  SIGNED_OUT_REVOKED,
} from '../utils/sessionLimits';

const AuthContext = createContext();

// What the set-up page names as the reason when the account is a system admin.
const SYSTEM_ADMIN_MFA_LABEL = 'TrakLog system administration';

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Force the stored workspace to agree with the one the token authorises.
 *
 * Also refreshes the cached role, name and code, which describe the *stored*
 * workspace and are wrong the moment it changes. Name and code are recovered
 * from the cached access list when it knows the workspace, and removed rather
 * than left stale when it doesn't — a wrong workspace name in the app bar is
 * worse than none.
 *
 * Called only when the token actually carries a subscriber_id. A token without
 * one is a system admin sitting on /system-admin with no workspace selected,
 * or a login still in flight; neither should have its stored workspace cleared.
 *
 * @returns {string|null} the reconciled workspace id, or null if it already agreed
 */
export function reconcileStoredWorkspace(tokenPayload, storage = localStorage) {
  const fromToken = tokenPayload?.subscriber_id;
  if (!fromToken) return null;
  if (storage.getItem('subscriberId') === fromToken) return null;

  storage.setItem('subscriberId', fromToken);

  const role = tokenPayload.role || 'user';
  storage.setItem('subscriberRole', role);

  let known = null;
  try {
    const cached = JSON.parse(storage.getItem('accessibleSubscribers') || '[]');
    known = Array.isArray(cached) ? cached.find(s => s && s.id === fromToken) : null;
  } catch (error) {
    known = null;   // a corrupt cache is not a reason to fail the reconcile
  }

  if (known?.name) storage.setItem('subscriberName', known.name);
  else storage.removeItem('subscriberName');

  if (known?.subscriberCode) storage.setItem('subscriberCode', known.subscriberCode);
  else storage.removeItem('subscriberCode');

  return fromToken;
}

// What an ID token says, for the questions the app asks of it. A token that
// cannot be read says nothing: every field is empty.
export const NO_TOKEN_CLAIMS = Object.freeze({
  subscriberId: null,
  role: null,
  systemAdmin: false,
  issuedAt: null,
});

export function claimsFromToken(token) {
  if (!token || typeof token !== 'string') return NO_TOKEN_CLAIMS;
  try {
    const payload = decodeJwtPayload(token);
    if (!payload) return NO_TOKEN_CLAIMS;
    return {
      subscriberId: payload.subscriber_id || null,
      role: payload.role || null,
      systemAdmin: payload.system_admin === true,
      issuedAt: typeof payload.iat === 'number' ? payload.iat : null,
    };
  } catch (error) {
    return NO_TOKEN_CLAIMS;
  }
}

/**
 * Can a workspace read be made for `subscriberId` with this token?
 *
 * `firestore.rules` checks `request.auth.token.subscriber_id`, so a read
 * started before the token carries the chosen workspace is refused, and a
 * listener that is refused stays dead. `subscriberId` changes the moment a
 * workspace is chosen; the claim follows only after set-custom-claims has
 * answered and the token has been refreshed. Readers that start on their own
 * (the root-mounted providers) wait for this rather than for `subscriberId`.
 *
 * A system admin's token passes every workspace rule these readers depend on
 * (`isSystemAdmin()`), so it is ready for any workspace.
 */
export function tokenCoversWorkspace(tokenClaims, subscriberId) {
  if (!subscriberId || !tokenClaims) return false;
  return tokenClaims.subscriberId === subscriberId || tokenClaims.systemAdmin === true;
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriberId, setSubscriberId] = useState(() => localStorage.getItem('subscriberId'));
  const [isSystemAdmin, setIsSystemAdmin] = useState(() => localStorage.getItem('systemAdmin') === 'true');
  const [userRole, setUserRole] = useState(null); // 'admin', 'manager', 'operator', 'customer'
  const [subscriberRole, setSubscriberRole] = useState(() => localStorage.getItem('subscriberRole'));
  // "View as" — a system admin can clamp the UI to a lesser role to see what a
  // manager or a plain user sees. Deliberately a *display* clamp: the token
  // still carries admin, so a system admin who spots a broken template setting
  // while simulating can still fix it without unwinding the session. It is not
  // a security boundary and must never be treated as one — the API and
  // firestore.rules go on enforcing the real claims.
  //
  // sessionStorage, not localStorage: simulation is a debugging posture, and it
  // should die with the tab rather than greet you days later as an unexplained
  // missing menu.
  const [simulatedRole, setSimulatedRoleState] = useState(
    () => sessionStorage.getItem('simulatedRole') || null,
  );
  const [customerInfo, setCustomerInfo] = useState(null);
  const [idToken, setIdToken] = useState(null);
  // What the current ID token carries, as opposed to what the app has chosen.
  // See tokenCoversWorkspace above.
  const [tokenClaims, setTokenClaims] = useState(NO_TOKEN_CLAIMS);
  const adoptTokenClaims = useCallback((token) => {
    const next = claimsFromToken(token);
    setTokenClaims((prev) => (
      prev.subscriberId === next.subscriberId
      && prev.role === next.role
      && prev.systemAdmin === next.systemAdmin
      && prev.issuedAt === next.issuedAt
        ? prev
        : next
    ));
  }, []);
  const clearTokenClaims = useCallback(() => setTokenClaims(NO_TOKEN_CLAIMS), []);
  const [accessibleSubscribers, setAccessibleSubscribers] = useState([]);
  const [customerAccessEntries, setCustomerAccessEntries] = useState([]);
  // True while resumePostLogin is fetching access-info and choosing a destination.
  // The /login route guard checks this so it doesn't flash /select-subscriber
  // when the user actually has a single subscriber and will land on /tasks.
  const [postLoginInProgress, setPostLoginInProgress] = useState(false);
  // A system admin with no authenticator set up. ProtectedRoute sends them to
  // /enroll-mfa from any page, which is what catches a browser that was
  // already signed in when the requirement arrived (sign-in is handled by
  // resumePostLogin). The API refuses them either way; this is the way out.
  const [mfaSetupRequired, setMfaSetupRequired] = useState(false);
  // When this session really began (token `auth_time`, seconds since epoch).
  // It does not move when the token refreshes, which is the point.
  const [authTime, setAuthTime] = useState(null);
  // One automatic sign-out per session, even if the listener fires again
  // while the sign-out is still in flight.
  const autoSignOutRef = useRef(false);
  const logoutRef = useRef(null);

  const signOutForReason = (reason) => {
    if (autoSignOutRef.current) return;
    autoSignOutRef.current = true;
    try {
      sessionStorage.setItem(SIGNED_OUT_REASON_KEY, reason);
    } catch (error) {
      // The sign-out matters more than the message explaining it.
    }
    if (logoutRef.current) logoutRef.current();
  };

  useEffect(() => {
    // Initialize accessible subscribers from localStorage
    const storedSubscribers = localStorage.getItem('accessibleSubscribers');
    if (storedSubscribers) {
      try {
        setAccessibleSubscribers(JSON.parse(storedSubscribers));
      } catch (error) {
        console.error('Error parsing accessible subscribers:', error);
      }
    }

    // Initialize customer access entries from localStorage
    const storedCustomerAccess = localStorage.getItem('customerAccessEntries');
    if (storedCustomerAccess) {
      try {
        setCustomerAccessEntries(JSON.parse(storedCustomerAccess));
      } catch (error) {
        console.error('Error parsing customer access entries:', error);
      }
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        autoSignOutRef.current = false;
        try {
          const token = await user.getIdToken(true); // Force refresh for latest claims
          setIdToken(token); // Store token in state for components
          adoptTokenClaims(token);

          // Parse user role and customer info from token claims
          const tokenPayload = decodeJwtPayload(token) || {};
          const tokenAuthTime = typeof tokenPayload.auth_time === 'number' ? tokenPayload.auth_time : null;
          setAuthTime(tokenAuthTime);
          // Judge a restored session before anything renders from it. The
          // stored flag covers a system admin whose claim is not on the token yet.
          if (isSessionExpired({
            authTime: tokenAuthTime,
            systemAdmin: tokenPayload.system_admin === true || localStorage.getItem('systemAdmin') === 'true',
          })) {
            signOutForReason(SIGNED_OUT_EXPIRED);
            setLoading(false);
            return;
          }
          const role = tokenPayload.role || 'user';
          setUserRole(role);

          // System admin status comes from the token claim, set by /auth/set-custom-claims
          // (which reads the canonical system_config/admins.admin_emails). resumePostLogin
          // also writes localStorage when called by Login.js / MFA / force-reset flows.
          const hasSystemAdminClaim = tokenPayload.system_admin === true;
          if (hasSystemAdminClaim) {
            setIsSystemAdmin(true);
            localStorage.setItem('systemAdmin', 'true');
          }

          if (role === 'customer') {
            setCustomerInfo({
              customerId: tokenPayload.customer_id,
              customerName: tokenPayload.customer_name,
              subscriberId: tokenPayload.subscriber_id,
              // The workspace that carried the work out, shown on every job
              // card. Absent from tokens minted before the claim existed, so
              // it falls back to '' and the card shows its dash — an existing
              // portal session picks the name up at the next refresh (≤1h)
              // rather than needing a re-login.
              subscriberName: tokenPayload.subscriber_name || ''
            });
            // For customer users, set the subscriber context
            setSubscriberId(tokenPayload.subscriber_id);
            localStorage.setItem('subscriberId', tokenPayload.subscriber_id);
          } else if (tokenPayload.subscriber_id) {
            // The token is the only workspace authority. `subscriberId` seeds
            // from localStorage, which scopes every Firestore query, while the
            // rules check request.auth.token.subscriber_id. Nothing used to
            // reconcile the two, and custom claims are global *per account*
            // while localStorage is per browser — so switching workspace in a
            // second tab, on a second machine, or on a shared test login
            // silently rewrote the claims out from under this session. The
            // queries then asked for one workspace holding a token for
            // another and every read came back "Missing or insufficient
            // permissions" (the Geofence Manager was where it surfaced,
            // because custom_locations is the one workspace-scoped collection
            // with no isSystemAdmin() branch to mask it).
            //
            // The token wins rather than re-claiming the stored workspace:
            // re-claiming would have two tabs fight, each reasserting its own
            // choice on every refresh. One account cannot be in two workspaces
            // at once, and this is the same rule the customer branch above has
            // always followed.
            const reconciled = reconcileStoredWorkspace(tokenPayload);
            if (reconciled) {
              console.warn(
                '[auth] Stored workspace did not match the token; following the '
                + 'token. The workspace was most likely switched in another tab '
                + 'or on another device using this same account.'
              );
              setSubscriberId(reconciled);
              setSubscriberRole(tokenPayload.role || 'user');
            }
          }

          // Who GA4 thinks this session is. Unconditional, and after the branch
          // above rather than beside the claims: `setAnalyticsUserContext` sets
          // the COMPLETE context and clears whatever it isn't given, so a system
          // admin landing on /system-admin with no `subscriber_id` has to reach
          // it too, or the previous session's workspace stays attached to their
          // events.
          //
          // Each branch has its own trustworthy source for the workspace name:
          //
          //  - staff: localStorage, which the reconcile above just made
          //    trustworthy — it follows the token or is removed.
          //  - customer: the token. /auth/set-custom-claims mints
          //    `subscriber_name` for customer-portal sessions specifically
          //    (api/.../auth.py), and it is the same claim the portal's job
          //    cards read a few lines above. localStorage is NOT usable here —
          //    the customer branch never reconciles it, so it may still name
          //    the workspace this user came from.
          //  - no workspace in the token: nothing to name, and nothing to keep.
          //
          // Right id with the wrong name is the one outcome worth avoiding — it
          // merges two tenants in a name-grouped report — so a branch with no
          // trustworthy name reports none rather than guessing.
          let analyticsSubscriberName;
          if (role === 'customer') {
            analyticsSubscriberName = tokenPayload.subscriber_name || undefined;
          } else if (tokenPayload.subscriber_id) {
            analyticsSubscriberName = localStorage.getItem('subscriberName') || undefined;
          }
          // The reported role, which is not always the app's role. A system
          // admin holding no workspace has no `role` claim at all — auth.py
          // mints exactly system_admin / email / display_name /
          // claims_updated_at for that case — so `role` above took its
          // `|| 'user'` fallback and filed the platform operator under the
          // lowest-privilege role in the app's own vocabulary. Report what they
          // actually are. `role` itself is untouched: it feeds setUserRole and
          // the permission surface.
          //
          // The condition is `!subscriber_id && system_admin`, not system_admin
          // alone: an admin who has chosen a workspace is doing admin work *in*
          // that workspace and should read as `admin` there. Only the
          // workspace-less platform-dashboard session is the operator qua
          // operator.
          //
          // Not a way to keep operator traffic out of the numbers: `role` is
          // user-scoped and GA4 resolves user-scoped dimensions to the user's
          // most recent value in the date range, so the moment this session
          // enters a workspace these events follow it to `admin`. Excluding
          // operator traffic is GA4's IP-based internal traffic filter —
          // console setup, not code. See the Task 6 runbook.
          const analyticsRole =
            !tokenPayload.subscriber_id && hasSystemAdminClaim ? 'system_admin' : role;
          setAnalyticsUserContext({
            subscriberId: tokenPayload.subscriber_id,
            subscriberName: analyticsSubscriberName,
            role: analyticsRole,
          });
        } catch (error) {
          console.error("Error getting ID token:", error);
          setIdToken(null); // Clear token on error
          clearTokenClaims();
          // No usable claims means no defensible identity. `currentUser` is
          // still set and `loading` flips false below, so the app renders and
          // keeps sending events — and every other identity transition either
          // sets a complete context or clears it. Leaving this one alone is the
          // single branch where the *previous* session's workspace survives
          // into the next, which is the whole failure mode this design exists
          // to prevent.
          setAnalyticsUserContext({});
        }
      } else {
        setAuthTime(null);
        setUserRole(null);
        setCustomerInfo(null);
        setIdToken(null); // Clear token when no user
        clearTokenClaims();
        // Nobody is signed in, so nothing should still be attributed to the
        // last person who was. Logout is an SPA navigation (MyAppBar →
        // navigate('/login'), no reload), so gtag's user properties outlive it
        // and would otherwise ride along on whatever fires on /login next.
        setAnalyticsUserContext({});
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Every new ID token, whoever asked for it: our own refreshes, the forced
  // refresh after set-custom-claims, and the SDK's hourly renewal that nothing
  // here sees otherwise. Keeps tokenClaims honest, which is what lets a read
  // refused for want of a claim try again as soon as the claim arrives.
  useEffect(() => {
    // Test doubles of firebase/auth often leave this out.
    if (typeof onIdTokenChanged !== 'function') return undefined;
    let unsubscribe;
    try {
      unsubscribe = onIdTokenChanged(auth, (user) => {
        if (!user) {
          clearTokenClaims();
          return;
        }
        Promise.resolve()
          .then(() => user.getIdToken())
          .then((token) => adoptTokenClaims(token))
          .catch(() => {
            // A token we cannot read now is read again at the next change.
          });
      });
    } catch (error) {
      console.warn('Could not watch for ID token changes:', error);
    }
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [adoptTokenClaims, clearTokenClaims]);

  // Auto-refresh token every 50 minutes to prevent expiration
  useEffect(() => {
    if (!currentUser) return;

    // Pull the workspace lists fresh alongside the token. They were previously
    // fetched only by resumePostLogin — i.e. at login and nowhere else — while
    // every other page load hydrated them from the localStorage cache seeded on
    // mount. Nothing ever invalidated that cache, so the workspace switcher kept
    // listing workspaces that had since been deleted (rendered as "N/A", the
    // literal fallback the API substitutes for a missing `name`) and never
    // picked up workspaces created after the last sign-in.
    //
    // On failure the cached values are deliberately left untouched: a transient
    // API error should not empty the switcher the user is looking at.
    const refreshAccessibleWorkspaces = async (token) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/users/me/access-info`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
          console.warn('Could not refresh accessible workspaces:', response.status);
          return;
        }

        const data = await response.json();
        const subs = data.accessibleSubscribers || [];
        const custAccess = data.customerAccess || [];

        localStorage.setItem('accessibleSubscribers', JSON.stringify(subs));
        setAccessibleSubscribers(subs);
        localStorage.setItem('customerAccessEntries', JSON.stringify(custAccess));
        setCustomerAccessEntries(custAccess);

        // Backfill the selected workspace's display fields. reconcileStoredWorkspace
        // clears `subscriberName` / `subscriberCode` when the token names a
        // workspace the cached access list has not seen yet, and nothing put them
        // back — so the app bar and every localStorage reader (user invites,
        // export filenames) went blank on a workspace that was correctly
        // selected. This runs after the authoritative list has just landed.
        const currentId = localStorage.getItem('subscriberId');
        const currentSub = currentId ? subs.find((s) => s.id === currentId) : null;
        if (currentSub) {
          if (currentSub.name) localStorage.setItem('subscriberName', currentSub.name);
          if (currentSub.subscriberCode) localStorage.setItem('subscriberCode', currentSub.subscriberCode);
          if (currentSub.role) localStorage.setItem('subscriberRole', currentSub.role);
        }

        // The selected workspace is gone but its id is still in localStorage,
        // where it is restored into state on every boot. AppLayout only asks
        // whether that value is truthy, so a dangling id rendered the full
        // workspace shell against a workspace that no longer exists — an
        // empty Tasks page that looks like a workspace with no tasks rather
        // than one that has been deleted. Clearing it sends the next render
        // to the picker.
        //
        // This runs for system admins too, and before the early return below.
        // Their list holds every live workspace, so a selection missing from
        // it is one deleted under them (the e2e harness purges its trial
        // workspace while the admin still has it open). It used to be skipped
        // for them, and the deleted workspace rendered as an empty one:
        // "Select Subscriber" in the app bar, a 0-of-4 checklist, and the
        // product tour starting by itself.
        const selected = localStorage.getItem('subscriberId');
        if (
          selected &&
          !subs.some((s) => s.id === selected) &&
          !custAccess.some((c) => c.subscriberId === selected)
        ) {
          console.warn('Clearing selected workspace: it is no longer accessible');
          localStorage.removeItem('subscriberId');
          localStorage.removeItem('subscriberName');
          localStorage.removeItem('subscriberRole');
          localStorage.removeItem('subscriberCode');
          setSubscriberId(null);
          setSubscriberRole(null);
        }

        // The server is the authority on system admin, and the cached flag has to
        // follow it both ways. It used to be OR-ed with localStorage and was only
        // ever set, never cleared, outside login and logout — so a flag left by a
        // system admin's session that ended without Log out survived into the
        // next account on that browser, which then got the System Admin menu
        // entry and "View as" for an account the API refuses on every call.
        if (data.systemAdmin) {
          localStorage.setItem('systemAdmin', 'true');
          setIsSystemAdmin(true);
          if (!data.mfaEnrolled) {
            sessionStorage.setItem('mfaRequiringOrgs', JSON.stringify([SYSTEM_ADMIN_MFA_LABEL]));
          }
          setMfaSetupRequired(!data.mfaEnrolled);
          // A system admin is listed against every workspace and is entitled to
          // have none selected, so neither check below applies to them (a
          // dangling selection was already cleared above).
          return;
        }
        if (localStorage.getItem('systemAdmin') === 'true') {
          localStorage.removeItem('systemAdmin');
          setIsSystemAdmin(false);
          sessionStorage.removeItem('simulatedRole');
          setSimulatedRoleState(null);
        }

        // Nothing left to be signed in to. The session survives a workspace
        // purge — the account is a global identity the purge deliberately
        // keeps — so this is the only thing that ends it for a user whose
        // token has not yet been revoked, or whose last role was removed by
        // hand rather than by a purge.
        if (subs.length === 0 && custAccess.length === 0) {
          console.warn('Signing out: this account no longer holds any workspace access');
          await logout();
          return;
        }

      } catch (error) {
        console.warn('Could not refresh accessible workspaces:', error);
      }
    };

    const refreshToken = async () => {
      try {
        const newToken = await currentUser.getIdToken(true);
        setIdToken(newToken);
        adoptTokenClaims(newToken);
        // A 30 day session cannot be held by a timer, so this tick is where
        // the age is checked. The effect below covers a system admin the
        // context only learns about from the access list.
        try {
          const refreshed = decodeJwtPayload(newToken) || {};
          if (isSessionExpired({
            authTime: refreshed.auth_time,
            systemAdmin: refreshed.system_admin === true || localStorage.getItem('systemAdmin') === 'true',
          })) {
            signOutForReason(SIGNED_OUT_EXPIRED);
            return;
          }
        } catch (error) {
          // An unreadable token is the API's call, not a reason to sign out here.
        }
        console.log('Auto-refreshed ID token');
        await refreshAccessibleWorkspaces(newToken);
      } catch (error) {
        console.error('Error auto-refreshing token:', error);
      }
    };

    // Initial refresh. This runs on every mount, which is what keeps the lists
    // fresh in practice — a 50-minute interval alone would never fire for a
    // session shorter than that, which is exactly how the stale entries
    // survived indefinitely.
    refreshToken();

    // Set up interval to refresh every 50 minutes
    const interval = setInterval(refreshToken, 50 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser]);

  const logout = () => {
    sessionStorage.removeItem('simulatedRole');
    setSimulatedRoleState(null);
    localStorage.removeItem('subscriberName');
    localStorage.removeItem('subscriberId');
    localStorage.removeItem('subscriberRole');
    localStorage.removeItem('subscriberCode');
    localStorage.removeItem('systemAdmin');
    localStorage.removeItem('customerAccessEntries');
    // Paired with customerAccessEntries above — this one was missed, so the
    // previous user's workspace list survived logout and sat in localStorage
    // until the next person finished signing in. Login overwrites it, so this
    // was never a correctness bug, but on a shared machine it is one account's
    // workspace names visible to whoever logs in next.
    localStorage.removeItem('accessibleSubscribers');
    sessionStorage.removeItem('effectiveSecurityPolicy');
    setIsSystemAdmin(false);
    setMfaSetupRequired(false);
    setCustomerAccessEntries([]);
    setAccessibleSubscribers([]);
    setUserRole(null);
    setSubscriberRole(null);
    setCustomerInfo(null);
    setSubscriberId(null);
    setIdToken(null); // Clear token on logout
    clearTokenClaims();
    return firebaseSignOut(auth);
  };

  logoutRef.current = logout;

  // A system admin is held to 24 hours, but the context may only learn that
  // after the token was read (the claim not yet on it). Judge again once known.
  // The limit can also pass while the page is open, and a timer cannot hold
  // 30 days, so look once a minute: without it the API would refuse every
  // call for up to 50 minutes before the token refresh noticed.
  useEffect(() => {
    if (!currentUser || authTime == null) return undefined;
    const check = () => {
      if (isSessionExpired({ authTime, systemAdmin: isSystemAdmin })) {
        signOutForReason(SIGNED_OUT_EXPIRED);
      }
    };
    check();
    const timer = setInterval(check, 60 * 1000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, authTime, isSystemAdmin]);

  // The API revokes the refresh tokens when access is removed and stamps
  // users/{uid}.sessions_revoked_at (epoch ms). Listening here ends the
  // session at once instead of at the next token refresh.
  const currentUid = currentUser?.uid;
  useEffect(() => {
    if (!currentUid || authTime == null) return undefined;
    let unsubscribe;
    try {
      unsubscribe = onSnapshot(
        doc(db, 'users', currentUid),
        (snapshot) => {
          // Some system admins have no user document: that means not revoked.
          if (!snapshot || !snapshot.exists || !snapshot.exists()) return;
          const sessionsRevokedAt = snapshot.data()?.sessions_revoked_at;
          if (isSessionRevoked({ authTime, sessionsRevokedAt })) {
            signOutForReason(SIGNED_OUT_REVOKED);
          }
        },
        (error) => {
          // Never sign anybody out because the listener failed.
          console.warn('Could not watch for revoked sessions:', error);
        },
      );
    } catch (error) {
      console.warn('Could not watch for revoked sessions:', error);
    }
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUid, authTime]);

  // Load subscriber role for current user.
  //
  // Canonical source of truth: `users/{uid}.rolesBySubscriber[subId]` —
  // the same field written by `POST /api/v1/users/add-user` and
  // `PUT /update-role`, and read by `GET /me/access-info` and
  // `GET /users/by-subscriber/{id}`. Previously this function read from
  // `subscribers/{subId}/subscriber_users/{uid}`, a legacy sub-collection
  // that nothing in the active write paths populates — so non-system-admin
  // admins (e.g. trial creators promoted via add-user / update-role) fell
  // through to the USER default and lost the Settings menu.
  //
  // Stored shape may be either a plain string ("admin") or a dict
  // ({role: "admin", status: "active"}); we accept both for parity with the
  // API's parsing logic.
  const loadSubscriberRole = async (subId) => {
    if (!currentUser || !subId) {
      setSubscriberRole(null);
      return null;
    }

    // System admins always act as admin on every subscriber, regardless of
    // explicit rolesBySubscriber entry (mirrors the access-info endpoint's
    // behaviour for system admins on subscribers they're not enrolled in).
    if (isSystemAdmin) {
      setSubscriberRole(SUBSCRIBER_ROLES.ADMIN);
      localStorage.setItem('subscriberRole', SUBSCRIBER_ROLES.ADMIN);
      return SUBSCRIBER_ROLES.ADMIN;
    }

    const writeRole = (role) => {
      setSubscriberRole(role);
      localStorage.setItem('subscriberRole', role);
      return role;
    };

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        return writeRole(SUBSCRIBER_ROLES.USER);
      }
      const entry = (userSnap.data().rolesBySubscriber || {})[subId];
      if (typeof entry === 'string') {
        return writeRole(entry);
      }
      if (entry && typeof entry === 'object' && typeof entry.role === 'string') {
        // A deactivated membership, or one in the recycle bin
        // (status "removed"), keeps its old role but grants nothing. The API
        // refuses both, so the UI should not offer what they cannot do.
        if ((entry.status || 'active') !== 'active') {
          return writeRole(SUBSCRIBER_ROLES.USER);
        }
        return writeRole(entry.role);
      }
      return writeRole(SUBSCRIBER_ROLES.USER);
    } catch (error) {
      console.error('Error loading subscriber role:', error);
      return writeRole(SUBSCRIBER_ROLES.USER);
    }
  };

  const setSimulatedRole = (role) => {
    if (role && !isSystemAdmin) return;          // only system admins may simulate
    setSimulatedRoleState(role || null);
    if (role) sessionStorage.setItem('simulatedRole', role);
    else sessionStorage.removeItem('simulatedRole');
  };

  // What the UI should render as. Every consumer of `subscriberRole` reads this,
  // so the clamp applies everywhere without each component knowing about it.
  const effectiveRole = simulatedRole || subscriberRole;

  // Check if current user has a specific permission
  const checkPermission = (permission) => {
    // While simulating, the system-admin shortcut is exactly what has to be
    // suppressed — otherwise a system admin sees admin UI whatever role they pick.
    if (grantsEverything(isSystemAdmin, simulatedRole)) return true;
    return hasPermission(effectiveRole, permission);
  };

  // Check if current user has any of the specified permissions
  const checkAnyPermission = (permissions) => {
    if (grantsEverything(isSystemAdmin, simulatedRole)) return true;
    return hasAnyPermission(effectiveRole, permissions);
  };

  // Check if current user's role is at least the specified role
  const checkRoleAtLeast = (role) => {
    if (grantsEverything(isSystemAdmin, simulatedRole)) return true;
    return isRoleAtLeast(effectiveRole, role);
  };

  // `authenticateCustomer` was removed 2026-09-03 along with the Cloud
  // Function behind it. It posted an email, customer id and subscriber id to
  // `authenticate_customer` and signed in with whatever custom token came
  // back — and that function never checked a password, so the call was an
  // account takeover primitive wearing a login's clothes. Nothing rendered it;
  // it was exported on the context and never invoked.
  //
  // Customer portal sign-in is the ordinary flow: Firebase email+password at
  // /login, then setCustomClaims(subscriberId, customerId) below, which
  // validates the pair against the caller's own customerAccess server-side.
  // See issues/2026-09-03-auth-access-control-audit.md (C1).

  // Get fresh ID token with optional force refresh
  const getIdToken = async (forceRefresh = true) => {
    if (currentUser) {
      return await currentUser.getIdToken(forceRefresh);
    }
    return null;
  };

  // Set custom claims for the user (supports both subscriber and customer claims)
  const setCustomClaims = async (subscriberId, customerId = null) => {
    // Wait for authentication to be ready
    let user = currentUser;

    if (!user) {
      // Wait up to 5 seconds for auth to be ready
      for (let i = 0; i < 50; i++) {
        await new Promise(resolve => setTimeout(resolve, 100));
        user = auth.currentUser;
        if (user) {
          break;
        }
      }
    }

    if (!user) {
      throw new Error('User not authenticated after waiting');
    }

    try {
      // Force-refresh so the token reflects the most recent sign-in state.
      // Without this, a cached pre-MFA token can slip through here when the
      // user selects a subscriber immediately after MFA and the API will 401.
      const token = await user.getIdToken(true);

      const body = { subscriber_id: subscriberId };
      if (customerId) {
        body.customer_id = customerId;
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/set-custom-claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(body)
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.detail || 'Failed to set custom claims');
      }

      // Force token refresh to get new claims and update state. The claims are
      // adopted here as well as by onIdTokenChanged so that a reader waiting on
      // tokenCoversWorkspace starts the moment this resolves.
      const newToken = await user.getIdToken(true);
      setIdToken(newToken);
      adoptTokenClaims(newToken);

      return responseData;
    } catch (error) {
      console.error('Error setting custom claims:', error);
      throw error;
    }
  };

  // Update subscriberId when localStorage changes.
  //
  // Awaits setCustomClaims so callers that navigate after this returns can
  // rely on the token already carrying `subscriber_id` / `role`. The
  // previous implementation fired set-custom-claims in a background then-
  // chain and returned immediately, which raced any post-login navigation:
  // the SPA would land on /tasks while the token was still claim-less,
  // and Firestore listeners (which read security rules off the token)
  // could deny reads until the token refreshed ~500ms later. Surfaced
  // by e2e/tests/test_role_api_gates — a REST sign-in immediately after
  // role-user provisioning would mint a tokenless-of-subscriber_id token
  // because the race had not yet resolved.
  //
  // Failure now propagates to the caller. The legacy fallback comment
  // ("will fall back to old Firestore-based permissions") referred to
  // pre-claims security rules that no longer exist — current rules
  // ONLY check custom claims, so a silent claims failure leaves the
  // user with a broken session anyway. Better to surface and let the
  // login UI show the error.
  const updateSubscriberId = async (newSubscriberId) => {
    // Roles are per workspace, so a simulation cannot meaningfully follow you
    // into another one — and silently carrying it over would be the worst
    // outcome: a restricted UI in a workspace you never chose to simulate.
    sessionStorage.removeItem('simulatedRole');
    setSimulatedRoleState(null);
    setSubscriberId(newSubscriberId);
    if (newSubscriberId) {
      localStorage.setItem('subscriberId', newSubscriberId);

      // Load the user's role for this subscriber. Intentionally not awaited:
      // it's a Firestore read used to seed local state for the AppBar etc.,
      // not a security gate. A miss just means the role chip flashes blank
      // for a moment.
      loadSubscriberRole(newSubscriberId);

      await setCustomClaims(newSubscriberId);
    } else {
      localStorage.removeItem('subscriberId');
      localStorage.removeItem('subscriberRole');
      setSubscriberRole(null);
    }
  };

  // Shared post-login routing logic. Called by Login.js, MfaEnrollment, and ForcePasswordReset.
  // Re-fetches access-info and handles all routing (force-reset, MFA enrollment, subscriber selection).
  const resumePostLogin = async (navigate) => {
    const user = auth.currentUser;
    if (!user) throw new Error('Not authenticated');

    // Suppress the /login route guard until we finish picking a destination.
    // Without this, clearing subscriberId below would let App.js redirect to
    // /select-subscriber for one render before this function navigates.
    setPostLoginInProgress(true);

    try {

    // Clear any leftover subscriber context from a prior session so the route
    // guard in App.js doesn't redirect to /tasks before we finish the
    // post-login routing decision below.
    localStorage.removeItem('subscriberId');
    localStorage.removeItem('subscriberName');
    localStorage.removeItem('subscriberRole');
    localStorage.removeItem('subscriberCode');
    setSubscriberId(null);
    setSubscriberRole(null);

    // Force-refresh so the token reflects the most recent sign-in state (e.g. the
    // MFA second factor just completed). A cached pre-MFA token can otherwise be
    // rejected by the API with 401.
    const token = await user.getIdToken(true);
    const response = await fetch(`${API_BASE_URL}/api/v1/users/me/access-info`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!response.ok) throw new Error('Failed to fetch access info');

    const data = await response.json();
    const subs = data.accessibleSubscribers || [];
    const custAccess = data.customerAccess || [];
    const isSysAdmin = data.systemAdmin || false;

    // Compute effective security policy and store for force-password-reset page
    const allPolicies = [
      ...subs.map(s => s.securityPolicy),
      ...custAccess.map(c => c.securityPolicy),
    ].filter(Boolean);
    const effectivePolicy = mergeStrictestPolicies(allPolicies);
    sessionStorage.setItem('effectiveSecurityPolicy', JSON.stringify(effectivePolicy));

    // Check force password reset — now subscriber-scoped
    const subsRequiringReset = subs.filter(s => s.forcePasswordReset);
    if (subsRequiringReset.length > 0) {
      sessionStorage.setItem('forcePasswordResetSubscribers', JSON.stringify(subsRequiringReset.map(s => s.id)));
      navigate('/force-password-reset');
      return;
    }

    // A system admin always needs an authenticator, whatever the workspaces
    // ask for. It is the same single enrollment a workspace policy asks for,
    // so one who is also a workspace admin sets it up once.
    const mfaRequired = isSysAdmin || effectivePolicy.require_mfa;

    // Check MFA required but not enrolled
    if (mfaRequired && !data.mfaEnrolled) {
      // Store names of orgs requiring MFA for the enrollment page. A system
      // admin is listed against every workspace, so naming the ones with a
      // policy would name workspaces they are not a member of.
      const mfaRequiringOrgs = isSysAdmin ? [SYSTEM_ADMIN_MFA_LABEL] : [
        ...subs.filter(s => s.securityPolicy?.require_mfa).map(s => s.name),
        ...custAccess.filter(c => c.securityPolicy?.require_mfa).map(c => c.customerName),
      ].filter(Boolean);
      if (mfaRequiringOrgs.length > 0) {
        sessionStorage.setItem('mfaRequiringOrgs', JSON.stringify(mfaRequiringOrgs));
      }
      navigate('/enroll-mfa');
      return;
    }

    setMfaSetupRequired(false);

    // Auto-unenroll from MFA if enrolled but nothing requires it
    if (data.mfaEnrolled && !mfaRequired) {
      try {
        const unenrollResp = await fetch(`${API_BASE_URL}/api/v1/security/unenroll-mfa`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (unenrollResp.ok) {
          console.log('Auto-unenrolled from MFA (server-side) — no subscriber requires it');
        } else {
          console.warn('Auto-unenroll from MFA failed:', await unenrollResp.text());
        }
      } catch (unenrollErr) {
        console.warn('Auto-unenroll from MFA failed:', unenrollErr);
      }
    }

    // Clear any leftover session data from MFA/force-reset flows
    sessionStorage.removeItem('effectiveSecurityPolicy');

    // Store system admin flag
    if (isSysAdmin) {
      localStorage.setItem('systemAdmin', 'true');
      setIsSystemAdmin(true);

      // Ensure the token has the system_admin claim. Calling set-custom-claims with no
      // subscriber_id promotes the user; the next getIdToken(true) picks up the claim.
      try {
        const claimsResp = await fetch(`${API_BASE_URL}/api/v1/auth/set-custom-claims`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({}),
        });
        if (claimsResp.ok) {
          const refreshed = await user.getIdToken(true);
          setIdToken(refreshed);
          adoptTokenClaims(refreshed);
        } else {
          console.warn('Failed to set system_admin claim:', await claimsResp.text());
        }
      } catch (e) {
        console.warn('Failed to set system_admin claim:', e);
      }
    } else {
      localStorage.removeItem('systemAdmin');
      setIsSystemAdmin(false);
    }

    // Store accessible subscribers in localStorage and context
    localStorage.setItem('accessibleSubscribers', JSON.stringify(subs));
    setAccessibleSubscribers(subs);

    // Store customer access entries in localStorage and context
    localStorage.setItem('customerAccessEntries', JSON.stringify(custAccess));
    setCustomerAccessEntries(custAccess);

    const totalEntries = subs.length + custAccess.length;

    // System admin → the platform dashboard, whether or not they also hold
    // workspace access. This used to be gated on `totalEntries === 0`, so an
    // operator who happened to be a member of one workspace was dropped into
    // that workspace's task list instead — the platform view they had actually
    // signed in for was two navigations away, and with several workspaces they
    // got the picker and had to choose a workspace before they could decline
    // to be in one. The system-admin routes need no subscriber context (see
    // RequireSystemAdmin), and the app-bar switcher still lists every workspace
    // for the trip in the other direction.
    if (isSysAdmin) {
      // No workspace, and nothing else will say so. The set-custom-claims call
      // above minted claims with neither `subscriber_id` nor `role` (auth.py),
      // but getIdToken(true) does not fire onAuthStateChanged and this path
      // never reloads — so GA4 keeps whatever was set at sign-in. Custom claims
      // are stored per account and survive a sign-out, so for an admin who last
      // worked in workspace X that is X's id, plus its name if they closed the
      // tab rather than logging out: a whole session of platform-dashboard
      // events attributed to a tenant they are not in.
      //
      // `subscriberId` and `subscriberName` are absent so they clear;
      // `system_admin` matches what the boot path reports for the same
      // workspace-less token (see the analytics block in onAuthStateChanged),
      // so a reload does not flip the property.
      //
      // Ordering across the two writers is timing, not structure. On a password
      // login onAuthStateChanged and this function race: the auth callback does
      // one getIdToken round trip while this one does access-info +
      // set-custom-claims + a refresh, so in practice this lands last and wins.
      // Nothing enforces it, and nothing should try to — a sequence counter
      // would be machinery guarding a margin measured in hundreds of
      // milliseconds. It is written down so the next reader does not assume an
      // ordering guarantee that was never made. Applies to all three branches
      // here.
      setAnalyticsUserContext({ role: 'system_admin' });
      navigate('/system-admin');
      return;
    }

    // No workspace, no customer portal, not a system admin — there is nothing
    // to sign in to. Sign back out before reporting it: sign-in has already
    // succeeded at this point, and leaving the session standing put the user
    // in the one state the app has no page for. The /login guard saw
    // `currentUser` with no subscriberId and bounced them to
    // /select-subscriber, whose "Back to Login" bounced them straight back —
    // and the app bar, which gates its nav on `currentUser` alone, offered a
    // working-looking Tasks page rendering an empty workspace. That is the
    // shape a purged workspace leaves its last admin in: the account survives
    // the purge by design, because a Firebase Auth account is a global
    // identity that may hold access elsewhere.
    if (totalEntries === 0) {
      await logout();
      throw new Error(NO_WORKSPACE_MESSAGE);
    }

    // Single subscriber, no customer access → auto-select.
    // Awaits updateSubscriberId so the navigate happens AFTER claims are
    // persisted server-side and the local token is refreshed; otherwise
    // /tasks renders against a claim-less token and Firestore listeners
    // briefly deny reads.
    if (subs.length === 1 && custAccess.length === 0) {
      const sub = subs[0];
      await updateSubscriberId(sub.id);
      localStorage.setItem('subscriberRole', sub.role);
      localStorage.setItem('subscriberCode', sub.subscriberCode);
      localStorage.setItem('subscriberName', sub.name);
      // The only workspace this session will ever be in, and nothing else will
      // announce it: onAuthStateChanged fires on sign-in/sign-out, not on the
      // token refresh that updateSubscriberId just did, and this path never
      // reloads the page. Without this, every event from a single-workspace
      // user carries whatever the pre-claims token happened to say.
      setAnalyticsUserContext({
        subscriberId: sub.id,
        subscriberName: sub.name,
        role: sub.role,
      });
      navigate('/tasks');
      return;
    }

    // Single customer access, no subscriber access → auto-select customer portal
    if (custAccess.length === 1 && subs.length === 0) {
      const entry = custAccess[0];
      const claimsResult = await setCustomClaims(entry.subscriberId, entry.customerId);
      adoptTokenClaims(await user.getIdToken(true));
      // Same reason as the workspace branch above — no reload, no second
      // onAuthStateChanged.
      //
      // Both values come from the claims just minted rather than from `entry`,
      // because the claims are what every later page load will report for this
      // same user (onAuthStateChanged reads them off the token):
      //  - role: 'customer', not `entry.role` — the access entry carries the
      //    *portal* permission level, which defaults to "viewer"
      //    (api/app/schemas/user.py), while the token sets role: 'customer'
      //    (auth.py). Using entry.role would emit `viewer` once and `customer`
      //    on every page load after, into the same GA4 user property.
      //  - the workspace name: set-custom-claims adds `subscriber_name` for
      //    customer-portal sessions, so a customer session does know the name
      //    of the workspace hosting it. Absent from tokens minted before that
      //    claim existed, hence the fallback to undefined.
      setAnalyticsUserContext({
        subscriberId: entry.subscriberId,
        subscriberName: claimsResult?.claims?.subscriber_name || undefined,
        role: 'customer',
      });
      navigate('/customer-portal');
      return;
    }

    // Multiple entries → unified picker
    const combinedList = [
      ...subs,
      ...custAccess.map(entry => ({
        id: entry.customerId,
        name: entry.customerName,
        role: entry.role,
        subscriberCode: '',
        subscriberId: entry.subscriberId,
        type: 'customer'
      }))
    ];
    navigate('/select-subscriber', { state: { accessibleSubscribers: combinedList } });
    } catch (error) {
      setPostLoginInProgress(false);
      throw error;
    }
    // Every path that gets here has called navigate(). The flag stays up
    // until the router has left /login (PostLoginRouteSettled lowers it).
    // Lowering it here, in the same tick as navigate(), let the /login route
    // guard render once more on /login and replace the destination: a user
    // sent to /force-password-reset ended on /tasks with the old password.
  };

  const claimsReady = tokenCoversWorkspace(tokenClaims, subscriberId);

  const value = {
    currentUser,
    idToken,
    // What the token carries ({ subscriberId, role, systemAdmin, issuedAt }),
    // and whether it already covers the chosen workspace. Readers that start
    // by themselves key on `claimsReady`, not on `subscriberId` alone.
    tokenClaims,
    claimsReady,
    getIdToken,
    loading,
    logout,
    subscriberId,
    setSubscriberId: updateSubscriberId,
    setCustomClaims,
    resumePostLogin,
    isSystemAdmin,
    userRole,
    // The clamped role — consumers get the simulated one while "view as" is on.
    subscriberRole: effectiveRole,
    // The role actually held, for the places that must not be fooled: the
    // view-as picker itself, and anything reporting who you really are.
    actualSubscriberRole: subscriberRole,
    simulatedRole,
    setSimulatedRole,
    loadSubscriberRole,
    checkPermission,
    checkAnyPermission,
    checkRoleAtLeast,
    customerInfo,
    accessibleSubscribers,
    setAccessibleSubscribers,
    customerAccessEntries,
    setCustomerAccessEntries,
    postLoginInProgress,
    setPostLoginInProgress,
    mfaSetupRequired,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}